/**
 * Who this app may email, and what the letter is called afterwards.
 *
 * The rule that matters here is the one about silence. `no_automated_contact`
 * means no automated email or SMS, and `blacklisted` forces it on by database
 * trigger (docs/BUSINESS-RULES.md). An email this app composes from a template
 * and sends on somebody's behalf is automated contact — the fact that a person
 * pressed the button does not make it a personal note — so both flags stop a
 * send here. A rep who genuinely needs to write to that customer can still do
 * it from Gmail, which is exactly the line the flags draw: the system goes
 * quiet, the people do not have to.
 *
 * It lives in core rather than in the send button because three things ask the
 * question and they must all get the same answer: the button, which greys
 * itself out and says why; the Edge Function, which re-checks before it hands
 * anything to Google; and the MCP server, where an agent could otherwise mail
 * a blacklisted customer through a tool that never thought to look.
 */

import type { Contact } from "./types.js";

export type EmailRefusalReason =
  "blacklisted" | "no_automated_contact" | "no_address" | "bad_address";

export interface EmailRefusal {
  reason: EmailRefusalReason;
  /** Said to whoever is holding the send button, so it names the way out. */
  message: string;
}

/**
 * Near enough. This is a typo check, not an assertion that mail will arrive —
 * that is only ever answered by sending — so it looks for the shape of an
 * address and nothing more. Rejecting an unusual but legal address would be a
 * worse failure than accepting one that bounces: the bounce comes back to the
 * rep's own inbox, where they will see it.
 */
export function looksLikeEmailAddress(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  if (/[\s<>,;"]/.test(trimmed)) return false;
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return false;
  const domain = trimmed.slice(at + 1);
  if (!domain.includes(".") || domain.startsWith(".") || domain.endsWith("."))
    return false;
  return !domain.includes("..");
}

/**
 * One recipient field, split into the addresses in it.
 *
 * Jonathon, 2026-09-01: "when we send emails to customers we need to be able
 * to type out additional emails in the recipient field." A roof is bought by
 * two people more often than one, and the second address — a spouse, a
 * property manager, the adjuster on the claim — was previously a second send
 * of the same letter, done by hand, or not done.
 *
 * Comma or semicolon, because both are what people type and because a mail
 * client accepts either. Empty pieces are dropped rather than treated as an
 * error: a trailing comma is how a half-finished list looks, not a mistake
 * worth stopping on.
 */
export function splitRecipients(value: string): string[] {
  return value
    .split(/[,;]/)
    .map((address) => address.trim())
    .filter(Boolean);
}

/** The contact fields this decision needs, so callers can pass a partial row. */
export type EmailableContact = Pick<
  Contact,
  "email" | "blacklisted" | "no_automated_contact"
>;

/**
 * Why this contact must not be emailed, or null if they may be.
 *
 * `address` overrides the one on the contact, for the case where a rep types a
 * different one into the compose box — the flags still apply, because they
 * belong to the person and not to the address. It may hold several addresses
 * separated by commas, and then every one of them has to look like an address:
 * a list is only as good as its worst entry, because the letter either goes to
 * all of them or to none.
 */
export function emailRefusal(
  contact: Partial<EmailableContact> | null | undefined,
  address?: string | null,
): EmailRefusal | null {
  if (contact?.blacklisted) {
    return {
      reason: "blacklisted",
      message:
        "This customer is blacklisted, so the app will not email them. " +
        "Clear the flag on their contact if that is wrong.",
    };
  }
  if (contact?.no_automated_contact) {
    return {
      reason: "no_automated_contact",
      message:
        "This customer is marked no automated contact, so the app will not " +
        "email them. A person can still call.",
    };
  }

  const chosen = (address ?? contact?.email ?? "").trim();
  const addresses = splitRecipients(chosen);
  if (addresses.length === 0) {
    return {
      reason: "no_address",
      message: "No email address on this contact. Add one and try again.",
    };
  }
  // Every one of them, and named. A list is typed in one box and read back as
  // one string, so "that does not look like an email address" about a field
  // holding three of them says nothing useful about which.
  const bad = addresses.find((one) => !looksLikeEmailAddress(one));
  if (bad !== undefined) {
    return {
      reason: "bad_address",
      message: `"${bad}" does not look like an email address.`,
    };
  }
  return null;
}

/** Shorthand for the button: may this go? */
export function canEmailContact(
  contact: Partial<EmailableContact> | null | undefined,
  address?: string | null,
): boolean {
  return emailRefusal(contact, address) === null;
}

/**
 * What the job's history calls an email after it has gone.
 *
 * The activity feed is read as a column of one-liners, and it is also what the
 * follow-up clock counts, so the subject is the useful part — "Emailed
 * dale@example.com — Your roof is scheduled" tells you what was said without
 * opening anything. The body is deliberately not repeated: it is several
 * hundred words of template and it would bury every other entry on the job.
 */
export function emailActivityBody(input: {
  to: string;
  subject: string;
}): string {
  const subject = input.subject.trim();
  const to = input.to.trim();
  return subject ? `Emailed ${to} — ${subject}` : `Emailed ${to}`;
}

/* -------------------------------------------------------------------------- */
/* Sending from somebody else's mailbox                                       */
/* -------------------------------------------------------------------------- */

/**
 * Whose Gmail an email actually leaves from.
 *
 * Jonathon, 2026-08-24: "when someone is sending an estimate they should have
 * the option to send it from someone else's email. for example if I want to
 * send an estimate for samuel i should be able to select samuel as the sender."
 *
 * That is a real widening and it is worth being plain about what it is. Until
 * now `gmail-send` looked up exactly one refresh token — the caller's own — so
 * "a rep cannot send as anyone but themselves" was not a rule the code checked,
 * it was a thing the code could not do. Making it possible means there is now a
 * rule to get right, and this is it:
 *
 *   - Anyone may send as themselves. Nothing changes for a rep.
 *   - An admin may send as anybody who has connected their Google account.
 *     That is the owner covering for a rep who is on a roof, which is the case
 *     he asked for.
 *   - Nobody else may send as anybody else, and asking is refused rather than
 *     quietly downgraded to sending as themselves. A silent downgrade would put
 *     the wrong name on a customer's estimate and tell nobody.
 *
 * Admin is the same gate as Tools → Users and Tools → Integrations, which is
 * where connections are managed in the first place. The decision is made here
 * so the button, the Edge Function and any agent reaching the endpoint all get
 * the same answer; the Edge Function keeps a transcription of it, bound by a
 * test, because it cannot import this package.
 */
export interface EmailSenderChoice {
  /** The profile whose Google connection sends the message. */
  profileId: string;
  /** True when that is not the person who pressed the button. */
  onBehalf: boolean;
}

export type EmailSenderDecision =
  | { ok: true; sender: EmailSenderChoice }
  | { ok: false; reason: "not_allowed" };

export function resolveEmailSender(input: {
  /** `profiles.id` of whoever is signed in. */
  callerProfileId: string;
  /** The profile they asked to send as. Absent means themselves. */
  requestedProfileId?: string | null;
  /** Holds the admin role, or `profiles.is_admin`. */
  callerIsAdmin: boolean;
}): EmailSenderDecision {
  const requested = (input.requestedProfileId ?? "").trim();
  if (!requested || requested === input.callerProfileId) {
    return {
      ok: true,
      sender: { profileId: input.callerProfileId, onBehalf: false },
    };
  }
  if (!input.callerIsAdmin) return { ok: false, reason: "not_allowed" };
  return { ok: true, sender: { profileId: requested, onBehalf: true } };
}

/** What a person is told when they ask to send as somebody they may not. */
export const SEND_AS_REFUSAL =
  "You can only send from your own connected Gmail. Ask an administrator to " +
  "send it from theirs.";

/**
 * The people this person may pick from, given who has connected Gmail.
 *
 * Their own connection first — it is the ordinary answer and should be the top
 * of the list — then everybody else by name. Somebody with no connection of
 * their own is not in the list at all: there is no token to send with, and
 * offering a name that cannot send is offering a failure.
 */
export interface ConnectedSender {
  profileId: string;
  emailAddress: string;
  name: string | null;
}

export function sendableAs(
  connected: readonly ConnectedSender[],
  input: { callerProfileId: string | null; callerIsAdmin: boolean },
): ConnectedSender[] {
  const mine = connected.filter(
    (row) => row.profileId === input.callerProfileId,
  );
  if (!input.callerIsAdmin) return mine;
  const others = connected
    .filter((row) => row.profileId !== input.callerProfileId)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  return [...mine, ...others];
}
